import { z } from 'zod';
import {
  CONNECTOR_TYPES,
  DEFAULT_CONNECTOR_TYPE,
  TWINGATE_NETWORK_DISPLAY_NAME_MAX_LENGTH,
  TWINGATE_NETWORK_SLUG_MAX_LENGTH,
  TWINGATE_NETWORK_SLUG_PATTERN,
} from './connector-manager.constants';

// New connector management schemas
export const CreateConnectorParamsSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[^-]+$/, 'Connector name cannot contain dashes (-)')
    .describe(
      'The name of the connector (max 80 characters, no dashes allowed)',
    ),
  connectorType: z
    .enum(CONNECTOR_TYPES)
    .default(DEFAULT_CONNECTOR_TYPE)
    .describe('The type of connector'),
  twingateNetworkId: z
    .string()
    .optional()
    .describe('Connector network ID (defaults to tenant default network)'),
});

export const CreateConnectorResultSchema = z.object({
  connectorId: z.string().describe('The ID of the created connector'),
  state: z.string().describe('The current health status of the connector'),
});

export const CreateConnectorRequestSchema = z.object({
  params: CreateConnectorParamsSchema,
});

export const CreateConnectorResponseSchema = z.object({
  result: CreateConnectorResultSchema,
});

export const ConnectorInfoSchema = z.object({
  connectorId: z.string().describe('The ID of the connector'),
  name: z.string().describe('The name of the connector'),
  state: z.string().describe('The current health status of the connector'),
  connectorType: z.string().describe('The type of connector'),
  createdAt: z.string().describe('When the connector was created'),
  lastSeen: z.string().optional().describe('When the connector was last seen'),
  twingateNetworkId: z
    .string()
    .optional()
    .describe('Connector network record ID'),
  twingateNetworkSlug: z.string().optional().describe('Connector network slug'),
  networkId: z.string().optional().describe('External network ID'),
  networkName: z.string().optional().describe('External network name'),
});

export const ListConnectorsQuerySchema = z.object({
  twingateNetworkId: z
    .string()
    .optional()
    .describe('Filter connectors by network ID'),
});

export const GetConnectorResponseSchema = z.object({
  result: ConnectorInfoSchema,
});

export const ListConnectorsResponseSchema = z.object({
  result: z.array(ConnectorInfoSchema).describe('List of connectors'),
});

// Token generation schemas
export const ConnectorTokensSchema = z.object({
  accessToken: z.string().describe('Access token for the connector'),
  refreshToken: z.string().describe('Refresh token for the connector'),
});

export const GenerateTokensResponseSchema = z.object({
  result: ConnectorTokensSchema,
});

export const TwingateResourcePortSchema = z
  .string()
  .trim()
  .min(1)
  .superRefine((value, ctx) => {
    if (value === '*') {
      return;
    }

    const parts = value.split(',').map((part) => part.trim());

    if (parts.length === 0 || parts.some((part) => part.length === 0)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Use * or ports like 80,443,8000-9000',
      });

      return;
    }

    for (const part of parts) {
      const match = /^(\d{1,5})(?:-(\d{1,5}))?$/.exec(part);

      if (!match) {
        ctx.addIssue({
          code: 'custom',
          message: 'Use * or ports like 80,443,8000-9000',
        });

        return;
      }

      const start = Number(match[1]);
      const end = match[2] != null ? Number(match[2]) : start;

      if (
        !Number.isInteger(start) ||
        !Number.isInteger(end) ||
        start < 1 ||
        end > 65535 ||
        end < start
      ) {
        ctx.addIssue({
          code: 'custom',
          message: 'Ports must be between 1 and 65535 with valid ranges',
        });

        return;
      }
    }
  })
  .describe('TCP ports: * for all, or 80,443,8000-9000');

const IPV4_CIDR_PATTERN =
  /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\/(?:[0-9]|[1-2]\d|3[0-2])$/;

const isNetworkAlignedIpv4Cidr = (value: string): boolean => {
  if (!IPV4_CIDR_PATTERN.test(value)) {
    return false;
  }

  const [ipPart, prefixPart] = value.split('/');
  const prefixLength = Number(prefixPart);
  const octets = ipPart.split('.').map(Number);

  if (octets.length !== 4 || octets.some((octet) => !Number.isInteger(octet))) {
    return false;
  }

  const address =
    (((octets[0] << 24) >>> 0) +
      ((octets[1] << 16) >>> 0) +
      ((octets[2] << 8) >>> 0) +
      (octets[3] >>> 0)) >>>
    0;
  const mask =
    prefixLength === 0 ? 0 : (0xffffffff << (32 - prefixLength)) >>> 0;

  return (address & mask) === address;
};

export const CreateTwingateResourceParamsSchema = z.object({
  name: z.string().min(1).describe('The name of the resource'),
  address: z
    .string()
    .min(1)
    .refine(
      (value) => {
        const trimmed = value.trim();

        if (!/^\d{1,3}(?:\.\d{1,3}){3}\/\d{1,2}$/.test(trimmed)) {
          return true;
        }

        return isNetworkAlignedIpv4Cidr(trimmed);
      },
      {
        error:
          'CIDR addresses must use the network address (e.g. 10.0.0.0/24, not 10.0.0.1/24)',
      },
    )
    .describe(
      'The address of the resource (IP, CIDR, FQDN, or wildcard domain)',
    ),
  port: TwingateResourcePortSchema.default('*').describe(
    'TCP ports allowed for the resource (* for all ports)',
  ),
  alias: z.string().optional().describe('Optional resource alias'),
  twingateNetworkId: z
    .string()
    .optional()
    .describe('Connector network ID (defaults to tenant default network)'),
});

export const CreateTwingateResourceResultSchema = z.object({
  resourceId: z.string().describe('The ID of the created resource'),
  name: z.string().describe('The name of the resource'),
  address: z.string().describe('The address of the resource'),
  port: z.string().describe('TCP ports configured on the resource'),
  alias: z.string().nullish().describe('The alias of the resource'),
  twingateNetworkId: z
    .string()
    .nullable()
    .optional()
    .describe('Connector network record ID'),
});

export const CreateTwingateResourceRequestSchema = z.object({
  params: CreateTwingateResourceParamsSchema,
});

export const CreateTwingateResourceResponseSchema = z.object({
  result: CreateTwingateResourceResultSchema,
});

export const TwingateResourceInfoSchema = z.object({
  resourceId: z.string().describe('The ID of the resource'),
  name: z.string().describe('The name of the resource'),
  address: z.string().describe('The address of the resource'),
  port: z.string().describe('TCP ports configured on the resource'),
  alias: z.string().nullish().describe('The alias of the resource'),
  networkId: z.string().describe('The network ID'),
  networkName: z.string().describe('The network name'),
  status: z.enum(['ACTIVE', 'INACTIVE']).describe('The status of the resource'),
  twingateNetworkId: z
    .string()
    .optional()
    .describe('Connector network record ID'),
  twingateNetworkSlug: z.string().optional().describe('Connector network slug'),
});

export const ListTwingateResourcesQuerySchema = z.object({
  twingateNetworkId: z
    .string()
    .optional()
    .describe('Filter resources by network ID'),
});

export const GetTwingateResourceResponseSchema = z.object({
  result: TwingateResourceInfoSchema,
});

export const ListTwingateResourcesResponseSchema = z.object({
  result: z.array(TwingateResourceInfoSchema).describe('List of resources'),
});

export const TwingateNetworkInfoSchema = z.object({
  id: z.string().describe('Connector network record ID'),
  slug: z.string().describe('URL-safe network slug unique per tenant'),
  displayName: z.string().describe('Admin-facing network label'),
  networkId: z.string().describe('External network ID'),
  networkName: z.string().describe('External network name'),
  isDefault: z.boolean().describe('Whether this is the tenant default network'),
  isActive: z.boolean().describe('Whether this network is active'),
  usesLegacyProxyNaming: z
    .boolean()
    .describe('Whether the network proxy uses legacy per-tenant naming'),
});

export const ListTwingateNetworksResponseSchema = z.object({
  result: z
    .array(TwingateNetworkInfoSchema)
    .describe('List of networks for the tenant'),
});

export const CreateTwingateNetworkParamsSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(TWINGATE_NETWORK_SLUG_MAX_LENGTH)
    .regex(
      TWINGATE_NETWORK_SLUG_PATTERN,
      'Slug must be lowercase alphanumeric with optional hyphens',
    ),
  displayName: z.string().min(1).max(TWINGATE_NETWORK_DISPLAY_NAME_MAX_LENGTH),
});

export const CreateTwingateNetworkRequestSchema = z.object({
  params: CreateTwingateNetworkParamsSchema,
});

export const CreateTwingateNetworkResponseSchema = z.object({
  result: TwingateNetworkInfoSchema,
});

export const GetTwingateNetworkResponseSchema = z.object({
  result: TwingateNetworkInfoSchema,
});

export const UpdateTwingateNetworkParamsSchema = z.object({
  displayName: z.string().min(1).max(80).optional(),
  isActive: z.boolean().optional(),
});

export const UpdateTwingateNetworkRequestSchema = z.object({
  params: UpdateTwingateNetworkParamsSchema,
});

export const UpdateTwingateNetworkResponseSchema = z.object({
  result: TwingateNetworkInfoSchema,
});

export const TwingateNetworkIdPathParamsSchema = z.object({
  networkId: z.string().describe('Connector network record ID'),
});

export const RedeployTwingateNetworkProxyResponseSchema = z.object({
  result: z.object({
    status: z.enum(['redeployed', 'skipped']),
  }),
});

export const CleanDeployTwingateNetworkProxyResponseSchema =
  RedeployTwingateNetworkProxyResponseSchema;
